import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, ToggleButtonGroup, ToggleButton, CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { getSoldProducts, getSaleHistory } from '../../databaseManager';
import { channelLabel, formatCurrency, parseStoredDate } from '../products/productUtils';

const RANGES = [
  { key: '7D', days: 7 },
  { key: '30D', days: 30 },
  { key: '90D', days: 90 },
  { key: 'All Time', days: null },
];

// saleHistory has no collectionGroup index/customerId field yet, so this fans
// out one read per sold product (same as iOS SalesMetricsDataModel) — capped
// so a big catalog doesn't open hundreds of requests at once.
async function loadSaleEntries(customer) {
  const products = await getSoldProducts(customer);
  const entries = [];
  const queue = [...products];
  const worker = async () => {
    while (queue.length) {
      const p = queue.shift();
      try {
        const history = await getSaleHistory(customer, p.productUri, parseStoredDate);
        history.forEach((e) => {
          if (!e.soldAtDate) return;
          entries.push({
            date: e.soldAtDate,
            channel: e.channel || '',
            brand: p.specs?.Brand || '',
            lineTotal: e.lineTotal || 0,
            lineCost: e.lineCost ?? null,
            quantity: e.quantitySold || 0,
          });
        });
      } catch (err) {
        console.error('Sale history fetch failed for', p.productUri, err);
      }
    }
  };
  await Promise.all(Array.from({ length: 8 }, worker));
  return entries;
}

const revenueOf = (items) => items.reduce((n, e) => n + e.lineTotal, 0);
const unitsOf = (items) => items.reduce((n, e) => n + e.quantity, 0);
const profitOf = (items) => items.reduce((n, e) => n + (e.lineTotal - (e.lineCost || 0)), 0);
const avgOf = (items) => { const u = unitsOf(items); return u > 0 ? revenueOf(items) / u : 0; };

// Revenue share per group: top 4, the rest folded into "Other" (iOS breakdown).
function breakdown(items, keyFn) {
  const total = revenueOf(items);
  if (total <= 0) return [];
  const groups = new Map();
  items.forEach((e) => groups.set(keyFn(e), (groups.get(keyFn(e)) || 0) + e.lineTotal));
  const slices = [...groups.entries()].map(([label, v]) => ({ label, percent: (v / total) * 100 })).sort((a, b) => b.percent - a.percent);
  if (slices.length <= 5) return slices;
  return [...slices.slice(0, 4), { label: 'Other', percent: slices.slice(4).reduce((n, s) => n + s.percent, 0) }];
}

function StatTile({ label, value, change }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>{value}</Typography>
      {change != null && (
        <Typography variant="caption" sx={{ color: change >= 0 ? 'success.main' : 'error.main', fontWeight: 600 }}>
          {change >= 0 ? '▲' : '▼'} {Math.abs(change).toFixed(1)}% vs previous period
        </Typography>
      )}
    </Paper>
  );
}

// Single-series daily revenue bars with a per-bar hover tooltip.
function RevenueChart({ days }) {
  const theme = useTheme();
  const [hover, setHover] = useState(null);
  if (days.length === 0) {
    return <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>Not enough sales in this range yet.</Typography>;
  }
  const width = 720;
  const height = 220;
  const pad = { top: 12, right: 8, bottom: 24, left: 56 };
  const max = Math.max(...days.map((d) => d.revenue), 1);
  const plotW = width - pad.left - pad.right;
  const plotH = height - pad.top - pad.bottom;
  const slot = plotW / days.length;
  const barW = Math.max(2, Math.min(28, slot - 2));
  const ticks = [0, 0.5, 1].map((f) => f * max);
  const fmtDay = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const labelEvery = Math.ceil(days.length / 8);

  return (
    <Box sx={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" role="img" aria-label="Revenue per day" style={{ display: 'block' }}>
        {ticks.map((t) => {
          const y = pad.top + plotH - (t / max) * plotH;
          return (
            <g key={t}>
              <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke={theme.palette.divider} strokeWidth={1} />
              <text x={pad.left - 6} y={y + 4} textAnchor="end" fontSize={11} fill={theme.palette.text.secondary}>
                {formatCurrency(t, { whole: true })}
              </text>
            </g>
          );
        })}
        {days.map((d, i) => {
          const h = Math.max(1, (d.revenue / max) * plotH);
          const x = pad.left + i * slot + (slot - barW) / 2;
          const y = pad.top + plotH - h;
          // Rounded only at the data end; flat on the baseline.
          const r = Math.min(4, barW / 2, h);
          return (
            <g key={d.date.toISOString()}>
              {/* Hit target is the full slot, bigger than the bar itself. */}
              <rect x={pad.left + i * slot} y={pad.top} width={slot} height={plotH} fill="transparent"
                onMouseEnter={() => setHover({ i, d })} onMouseLeave={() => setHover(null)} />
              <path
                d={`M${x},${y + h} V${y + r} Q${x},${y} ${x + r},${y} H${x + barW - r} Q${x + barW},${y} ${x + barW},${y + r} V${y + h} Z`}
                fill={theme.palette.primary.main}
                opacity={hover && hover.i !== i ? 0.45 : 1}
                pointerEvents="none"
              />
              {i % labelEvery === 0 && (
                <text x={pad.left + i * slot + slot / 2} y={height - 6} textAnchor="middle" fontSize={11} fill={theme.palette.text.secondary}>
                  {fmtDay(d.date)}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      {hover && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute', top: 0, pointerEvents: 'none', px: 1.25, py: 0.75,
            left: `${((pad.left + hover.i * slot + slot / 2) / width) * 100}%`, transform: 'translateX(-50%)',
          }}
        >
          <Typography variant="caption" color="text.secondary" component="div">{fmtDay(hover.d.date)}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrency(hover.d.revenue)}</Typography>
        </Paper>
      )}
    </Box>
  );
}

function BreakdownCard({ title, slices }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>{title}</Typography>
      {slices.length === 0 ? (
        <Typography variant="body2" color="text.secondary">Not enough sales in this range yet.</Typography>
      ) : slices.map((s) => (
        <Box key={s.label} sx={{ mb: 1.25 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
            <Typography variant="body2">{s.label}</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.percent.toFixed(0)}%</Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
            <Box sx={{ width: `${s.percent}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 4 }} />
          </Box>
        </Box>
      ))}
    </Paper>
  );
}

/** Sales Metrics — web port of the iOS SalesMetricsView. */
export default function SalesMetricsPage() {
  const { customer } = useParams();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangeKey, setRangeKey] = useState('7D');

  useEffect(() => {
    setLoading(true);
    loadSaleEntries(customer)
      .then(setEntries)
      .catch((err) => console.error('Failed to load sales metrics:', err))
      .finally(() => setLoading(false));
  }, [customer]);

  const metrics = useMemo(() => {
    const range = RANGES.find((r) => r.key === rangeKey);
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const start = range.days ? now - range.days * dayMs : null;
    const prevStart = range.days ? start - range.days * dayMs : null;
    const current = start ? entries.filter((e) => e.date.getTime() >= start) : entries;
    const previous = start ? entries.filter((e) => e.date.getTime() >= prevStart && e.date.getTime() < start) : [];
    // No comparison for All Time or against a zero baseline.
    const change = (cur, prev) => (range.days && prev !== 0 ? ((cur - prev) / prev) * 100 : null);

    const byDay = new Map();
    current.forEach((e) => {
      const d = new Date(e.date.getFullYear(), e.date.getMonth(), e.date.getDate());
      byDay.set(d.getTime(), (byDay.get(d.getTime()) || 0) + e.lineTotal);
    });
    const days = [...byDay.entries()].sort((a, b) => a[0] - b[0]).map(([t, revenue]) => ({ date: new Date(t), revenue }));

    return {
      revenue: revenueOf(current),
      units: unitsOf(current),
      profit: profitOf(current),
      avg: avgOf(current),
      revenueChange: change(revenueOf(current), revenueOf(previous)),
      unitsChange: change(unitsOf(current), unitsOf(previous)),
      profitChange: change(profitOf(current), profitOf(previous)),
      avgChange: change(avgOf(current), avgOf(previous)),
      days,
      byMarketplace: breakdown(current, (e) => channelLabel(e.channel || 'off_platform')),
      // "Unknown" is the backend's fallback for an undeterminable brand — fold into Other.
      byBrand: breakdown(current, (e) => (!e.brand.trim() || e.brand.trim().toLowerCase() === 'unknown' ? 'Other' : e.brand.trim())),
    };
  }, [entries, rangeKey]);

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, minWidth: 0 }}>
      <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate(`/sales/${customer}`)} sx={{ textTransform: 'none', mb: 1 }}>
        Sales
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 220 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Sales Metrics</Typography>
          <Typography variant="body2" color="text.secondary">Track your performance and spot opportunities to sell more.</Typography>
        </Box>
        <ToggleButtonGroup size="small" exclusive value={rangeKey} onChange={(_, v) => v && setRangeKey(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, fontWeight: 600 } }}>
          {RANGES.map((r) => <ToggleButton key={r.key} value={r.key}>{r.key}</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
            <StatTile label="Revenue" value={formatCurrency(metrics.revenue, { whole: true })} change={metrics.revenueChange} />
            <StatTile label="Items sold" value={metrics.units} change={metrics.unitsChange} />
            <StatTile label="Profit" value={formatCurrency(metrics.profit, { whole: true })} change={metrics.profitChange} />
            <StatTile label="Avg. sale price" value={formatCurrency(metrics.avg)} change={metrics.avgChange} />
          </Box>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Sales over time</Typography>
            <RevenueChart days={metrics.days} />
          </Paper>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
            <BreakdownCard title="Sales by marketplace" slices={metrics.byMarketplace} />
            <BreakdownCard title="Sales by brand" slices={metrics.byBrand} />
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
            Profit counts only the costs entered on each sale; sales without a cost count their full price.
          </Typography>
        </>
      )}
    </Box>
  );
}
